import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Mvpd, MvpdAuthRequest, TokenResponse, tveService } from '#/client';
import { StorageKeys } from '#/service/constants';
import { ApiError } from '#/service/error/errors';
import { getDeviceInfo } from '#/service/user/userSessionManager';
import { ignoreError, noop, techSearchParams, uniqBy } from '#/service/utils';
import { authActions } from '#/store/auth';

// 10 seconds polling interval to be used
const POLLING_MS = 10 * 1000;

const baseAuthRequest: MvpdAuthRequest = {
  networkInfo: {
    type: 'WIFI', // We can't reliably get this from the browser. So we'll just assume WIFI. It feels it does not affect functionality
  },
  deviceInfo: getDeviceInfo(),
};

/**
 * Handles TVE authentication process.
 * @see https://docs.one.accedo.io/one-core/delivery-api/identity/tve/#flow
 * @param [closeModal] {Function} Callback to close the modal. Default noop.
 * @returns API to work with the authentication process on the Login page
 */
export function useLoginTve(closeModal: () => void = noop) {
  const [mvpds, setMvpds] = useState<GroupedMvpds>();
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState<boolean>(true);
  const [awaiting, setAwaiting] = useState<boolean>(false);

  const logUserIn = useCallback((authResponse: TokenResponse) => {
    authActions.login(authResponse).finally(() => {
      setAwaiting(false);
      closeModal();
    });
    // `closeModal` is not here on purpose. closeModal has a new reference every time,
    // so it would generate an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);

    tveService
      .authenticate(baseAuthRequest)
      .then((res) => {
        const { type, auth, secondScreen } = res.data._meta;

        if (type === 'success') {
          return logUserIn(auth!);
        }

        // we'll need this to complete the flow after the redirect
        if (secondScreen?.authorizationCode) {
          sessionStorage.setItem(
            StorageKeys.TVE_CODE,
            secondScreen.authorizationCode
          );
        }

        return tveService
          .getMvpds()
          .then((res) => setMvpds(groupMvpds(res.data.mvpds)));
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [logUserIn]);

  useEffect(() => {
    // Do a polling loop to check if the user is authenticated
    let interval: ReturnType<typeof setInterval>;

    if (awaiting) {
      interval = setInterval(() => {
        tveService
          .authenticate(baseAuthRequest)
          .then((res) => {
            const meta = res.data._meta;

            if (meta.type === 'success') {
              logUserIn(meta.auth!);
            } else if (meta.type === 'redirect') {
              restartProcess();
            }
          })
          .catch((err) => {
            setError(err);
            setAwaiting(false);
          });
      }, POLLING_MS);
    }

    return () => clearInterval(interval);
  }, [awaiting, closeModal, logUserIn]);

  /**
   * Starts authentication process. It opens a new tab and starts polling to get the authentication back
   * @param mvpdId Selected MVPD
   */
  function selectProvider(mvpdId: string) {
    if (!mvpdId) {
      return;
    }

    setAwaiting(true);
    tveService
      .authenticate({ ...baseAuthRequest, mvpdId })
      .then((res) => {
        const meta = res.data._meta;
        const targetUrl = meta.redirect?.targetUrl;

        // we'll need this to complete the flow after the redirect
        sessionStorage.setItem(StorageKeys.TVE_MVPD, mvpdId);

        if (meta.type === 'redirect') {
          if (targetUrl) {
            window.location.href = targetUrl;
          } else {
            return Promise.reject(
              `URL for a redirect wasn't provided from the Core`
            );
          }
        } else {
          console.error(`We don't support authentication method ${meta.type}`);

          return Promise.reject(
            `Unsupported authentication method ${meta.type}`
          );
        }
      })
      .catch((err) => setError(err));
  }

  function restartProcess() {
    setAwaiting(false);
  }

  return {
    mvpds,
    error,
    loading,
    selectProvider,
    awaiting,
    restartProcess,
  };
}

function groupMvpds(allMvpds: Mvpd[]): GroupedMvpds {
  return {
    promoted: uniqBy(
      allMvpds.filter((mvpd) => mvpd.promoted),
      (el) => el.id
    ),
    others: uniqBy(allMvpds, (el) => el.id),
  };
}

interface GroupedMvpds {
  promoted: Mvpd[];
  others: Mvpd[];
}

/**
 * The TVE login flow includes a redirect to an external site. After the external login, the user is redirected back
 * to us, with `one_status=success`. That will be a new page load, so all our state will be lost. This hook listens for
 * this case, gets the required data back from session storage, and uses it to complete the login flow. This is what
 * gets the ONE tokens saved, and the user fully logged in.
 * @see https://docs.one.accedo.io/one-core/delivery-api/identity/tve/#flow
 * @returns void
 */
export const useLoginTveComplete = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // check the one_status query param
    const params = new URLSearchParams(search);
    const oneStatus = params.get('one_status');

    if (oneStatus !== 'success') {
      return;
    }

    // load & remove the values stored before the redirect
    const authorizationCode = sessionStorage.getItem(StorageKeys.TVE_CODE);
    const mvpdId = sessionStorage.getItem(StorageKeys.TVE_MVPD);
    sessionStorage.removeItem(StorageKeys.TVE_CODE);
    sessionStorage.removeItem(StorageKeys.TVE_MVPD);

    if (!(authorizationCode && mvpdId)) {
      return;
    }

    // complete the TVE flow
    tveService
      .authenticate({ ...baseAuthRequest, mvpdId, authorizationCode })
      .then((res) => {
        const { type, auth } = res.data._meta;

        if (type !== 'success') {
          throw new Error(`Unexpected response type: ${type}`);
        }

        if (!auth) {
          throw new Error('No auth object received');
        }

        // log in the user
        return authActions.login(auth);
      })
      .catch(ignoreError)
      .then(() => {
        // successful or not, remove the one_status query param
        navigate(
          {
            pathname: pathname,
            search: techSearchParams(search),
          },
          { replace: true }
        );
      })
      .catch(ignoreError);
  }, [navigate, pathname, search]);
};
